/**
 * workerFileStorage.ts
 *
 * All file operations go through the canonical relative endpoints under /api/files/*.
 * No direct Cloudflare Worker calls, no D1_WORKER_TOKEN, and no raw SQL is permitted.
 */

import { apiDelete } from './api';

export interface WorkerUploadResponse {
  success: boolean;
  fileId: string;
  key: string;
  name: string;
  size: number;
  type: string;
  fileUrl?: string;
  error?: string;
}

/**
 * Checks whether a file is a video file. Video binary uploads are prohibited.
 */
export function isVideoFile(file: File): boolean {
  if (!file) return false;
  const name = file.name.toLowerCase();
  const mime = file.type.toLowerCase();
  const videoExts = ['.mp4', '.mov', '.webm', '.mkv', '.avi', '.flv', '.wmv', '.m4v', '.3gp'];
  if (mime.startsWith('video/')) return true;
  return videoExts.some(ext => name.endsWith(ext));
}

/**
 * Formats byte size into readable string.
 */
export function formatFileSize(bytes: number, decimals = 2): string {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Uploads a file using the canonical direct-to-worker upload flow:
 * Ticket Request -> Direct Worker Upload -> Server Finalize.
 * Reused across Learning Assistant, Weight Progress Photos, and general modules.
 */
export async function uploadFileToWorker(
  file: File,
  module: string = 'general',
  parentId?: string
): Promise<WorkerUploadResponse> {
  if (isVideoFile(file)) {
    throw new Error('آپلود مستقیم فایل‌های ویدیو به علت حجم بالا پشتیبانی نمی‌شود. لطفاً از گزینه «اشتراک‌گذاری لینک ویدیو» استفاده فرمایید.');
  }

  const isWeightPhoto = module === 'weight' || module === 'weight_photos';
  const MAX_FILE_SIZE = isWeightPhoto ? 614400 : 50 * 1024 * 1024; // 600 KB for weight photos, 50 MB canonical limit
  if (file.size > MAX_FILE_SIZE) {
    if (isWeightPhoto) {
      throw new Error(`حجم تصویر (${formatFileSize(file.size)}) بیش از حد مجاز ۶۰۰ کیلوبایت است.`);
    }
    throw new Error(`حجم فایل انتخاب شده (${formatFileSize(file.size)}) بیش از حد مجاز (۵۰ مگابایت) است (FILE_TOO_LARGE).`);
  }

  if (isWeightPhoto) {
    console.log("[Diagnostic Weight Upload Path]:", {
      module,
      fileSize: file.size,
      uploadPath: "ticket"
    });
  }

  const doDirectUpload = async (): Promise<WorkerUploadResponse> => {
    // 1. Request Short-Lived Upload Ticket
    const ticketResponse = await fetch('/api/files/ticket', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        module,
        parentId: parentId || undefined,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type || 'application/octet-stream'
      }),
      credentials: 'include'
    });

    if (!ticketResponse.ok) {
      let errCode = '';
      let errMessage = '';
      try {
        const errData = await ticketResponse.json();
        errCode = errData?.error?.code || '';
        errMessage = errData?.error?.message || errData?.message || '';
      } catch {
        errMessage = await ticketResponse.text().catch(() => '');
      }

      if (ticketResponse.status === 413 || errCode === 'FILE_TOO_LARGE') {
        throw new Error(isWeightPhoto ? 'حجم تصویر بیشتر از سقف مجاز ۶۰۰ کیلوبایت است (FILE_TOO_LARGE).' : 'حجم فایل بیشتر از سقف مجاز ۵۰ مگابایت است (FILE_TOO_LARGE).');
      }
      if (ticketResponse.status === 401 || errCode === 'UNAUTHORIZED') {
        throw new Error('دسترسی غیرمجاز - لطفاً مجدداً وارد شوید (UNAUTHORIZED).');
      }
      if (ticketResponse.status === 403 || errCode === 'FORBIDDEN' || errCode === 'INVALID_PARENT') {
        throw new Error('دسترسی به شناسه مورد نظر غیرمجاز یا نامعتبر است (FORBIDDEN / INVALID_PARENT).');
      }
      throw new Error(errMessage || `خطا در دریافت بلیت آپلود (کد ${ticketResponse.status})`);
    }

    const ticketJson = await ticketResponse.json();
    if (!ticketJson?.success || !ticketJson?.data) {
      throw new Error(ticketJson?.error?.message || 'پاسخ نامعتبر از سرور صدور بلیت آپلود دریافت شد.');
    }

    const { fileId, uploadUrl, uploadTicket } = ticketJson.data;

    // 2. Direct Upload to Cloudflare File Worker with signed upload ticket
    const workerFormData = new FormData();
    workerFormData.append('file', file);

    const workerResponse = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${uploadTicket}`
      },
      body: workerFormData
    });

    if (!workerResponse.ok) {
      let workerErrCode = '';
      let workerErrMsg = '';
      try {
        const wErrData = await workerResponse.json();
        workerErrCode = wErrData?.error?.code || '';
        workerErrMsg = wErrData?.error?.message || wErrData?.error || '';
      } catch {
        workerErrMsg = await workerResponse.text().catch(() => '');
      }

      if (workerResponse.status === 409 || workerErrCode === 'TICKET_ALREADY_CONSUMED') {
        throw new Error('بلیت آپلود قبلاً استفاده شده است یا منقضی شده است (TICKET_ALREADY_CONSUMED).');
      }
      if (workerResponse.status === 413 || workerErrCode === 'FILE_TOO_LARGE') {
        throw new Error(isWeightPhoto ? 'حجم تصویر بیشتر از سقف مجاز ۶۰۰ کیلوبایت است (FILE_TOO_LARGE).' : 'حجم فایل بیشتر از سقف مجاز ۵۰ مگابایت است (FILE_TOO_LARGE).');
      }
      throw new Error(workerErrMsg || `خطای آپلود در سرویس ابری R2 (کد ${workerResponse.status})`);
    }

    const workerJson = await workerResponse.json().catch(() => ({}));

    // 3. Finalize upload and activate metadata in D1
    const finalizeResponse = await fetch('/api/files/finalize', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ fileId }),
      credentials: 'include'
    });

    if (!finalizeResponse.ok) {
      let finErrCode = '';
      let finErrMsg = '';
      try {
        const finErrData = await finalizeResponse.json();
        finErrCode = finErrData?.error?.code || '';
        finErrMsg = finErrData?.error?.message || finErrData?.message || '';
      } catch {
        finErrMsg = await finalizeResponse.text().catch(() => '');
      }
      throw new Error(finErrMsg || `خطا در نهایی‌سازی ثبت فایل در پایگاه داده (کد ${finalizeResponse.status})`);
    }

    const finalizeJson = await finalizeResponse.json();
    const activeData = finalizeJson?.data || {};

    if (typeof window !== 'undefined') {
      const g = window as any;
      g.__registeredFileIds = g.__registeredFileIds || new Set<string>();
      g.__registeredFileIds.add(fileId);
    }

    return {
      success: true,
      fileId,
      key: activeData.r2Key || workerJson.key || '',
      name: activeData.fileName || file.name,
      size: activeData.fileSize || file.size,
      type: activeData.mimeType || file.type,
      fileUrl: activeData.downloadUrl || `/api/files/${fileId}/download`
    };
  };

  try {
    return await doDirectUpload();
  } catch (err: any) {
    console.error('[Direct Upload Error]:', err);
    throw err;
  }
}

/**
 * Deletes a file from local server API.
 */
export async function deleteFileFromWorker(fileId: string): Promise<boolean> {
  if (!fileId) return false;
  try {
    const res = await apiDelete(`/api/files/${fileId}`);
    return res.success;
  } catch (err) {
    console.warn('[File Delete Error]:', err);
    return false;
  }
}

/**
 * Returns the relative or absolute download URL.
 */
export function getWorkerFileUrl(r2KeyOrFileId?: string, fallbackUrl?: string): string {
  if (!r2KeyOrFileId) return fallbackUrl || '';

  // If it's already a full URL, return as-is
  if (r2KeyOrFileId.startsWith('http://') || r2KeyOrFileId.startsWith('https://')) {
    return r2KeyOrFileId;
  }

  // If it's already a relative /api/ route, return as-is
  if (r2KeyOrFileId.startsWith('/api/')) {
    return r2KeyOrFileId;
  }

  // Try extracting UUID if present anywhere in the key/path (e.g. uploads/uuid-filename or users/uid/files/uuid-filename)
  const uuidMatch = r2KeyOrFileId.match(/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/);
  if (uuidMatch && uuidMatch[1]) {
    return `/api/files/${uuidMatch[1]}/download`;
  }

  // Otherwise, treat as direct fileId
  return `/api/files/${r2KeyOrFileId}/download`;
}
